import React, { useState, useEffect, Key } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate, useResolvedPath } from "react-router-dom";
import {
  SelectableList,
  SelectableListDataType,
} from '../../../../../shared/selectable-list/selectable-list';
import {
  StaffActiveCaseListContainerCredentialVerificationCasesV1ByApplicantDocument,
  StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument,
  StaffActiveCaseListContainerSendReportCasesV1ByApplicantDocument,
} from "../../../../../../generated";
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader'
import { Empty } from "antd";
import { DATE_TIME_FORMAT, FormatDate } from "../../../../../../constants";

interface ActiveCaseListCommonContainerProps {
  applicantId: string;
  isVerticalColumn: boolean | undefined;
  refetch?: boolean | undefined;
}
const formatCamelCase = (camelCaseString: string) => {
  return camelCaseString
    .replace(/caseWorker/g, 'Caseworker') 
    .replace(/([a-z])([A-Z])/g, '$1 $2') 
    .replace(/^./, str => str.toUpperCase()); 
};


export const ActiveCaseListCommonContainer: React.FC<ActiveCaseListCommonContainerProps> = ({ refetch, applicantId, isVerticalColumn }) => {
  const [selectedCase, setSelectedCase] = useState<
    SelectableListDataType | undefined
  >();
  const navigate = useNavigate();

  const {
    data: identityVerificationCasesV1Data,
    loading: identityVerificationCasesV1Loading,
    error: identityVerificationCasesV1Error,
    refetch: refetchIdentityVerification,
  } = useQuery(
    StaffActiveCaseListContainerIdentityVerificationCasesV1ByApplicantDocument,
    {
      variables: { applicantId },
      skip: !applicantId, // Skip the query if applicantId is not available
    }
  );

  const {
    data: credentialVerificationCasesV1Data,
    loading: credentialVerificationCasesV1Loading,
    error: credentialVerificationCasesV1Error,
    refetch: refetchCredentialVerification,
  } = useQuery(
    StaffActiveCaseListContainerCredentialVerificationCasesV1ByApplicantDocument,
    {
      variables: { applicantId },
      skip: !applicantId,
    }
  );

  const {
    data: sendReportCasesV1Data,
    loading: sendReportCasesV1Loading,
    error: sendReportCasesV1Error,
    refetch: refetchSendReport,
  } = useQuery(
    StaffActiveCaseListContainerSendReportCasesV1ByApplicantDocument,
    {
      variables: { applicantId },
      skip: !applicantId,
    }
  );

  useEffect(() => {
    if (refetch) {
      refetchIdentityVerification();
      refetchCredentialVerification();
      refetchSendReport();
    }
  }, [refetch]);

  const formattedData: SelectableListDataType[] = [
    ...(identityVerificationCasesV1Data?.identityVerificationCasesV1ByApplicant?.map(
      (item) => ({
        key: item?.id,
        caseType: item?.caseType,
        title: item?.caseName || "",
        initials: item?.caseName?.substring(0, 2).toUpperCase() || "",
        progress: formatCamelCase(item?.state ?? ""),
        schemaVersion: "",
        timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
        state: item?.state || "",
      })
    ) || []),
    ...(credentialVerificationCasesV1Data?.credentialVerificationCasesV1ByApplicant?.map(
      (item) => ({
        key: item?.id,
        caseType: item?.caseType,
        title: item?.caseName || "",
        initials: item?.caseName?.substring(0, 2).toUpperCase() || "",
        progress: formatCamelCase(item?.state ?? ""),
        schemaVersion: "",
        timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
        state: item?.state || "",
      })
    ) || []),
    ...(sendReportCasesV1Data?.sendReportCasesV1ByApplicant?.map((item) => ({
      key: item?.id,
      caseType: item?.caseType,
      title: item?.caseName || "",
      initials: item?.caseName?.substring(0, 2).toUpperCase() || "",
      progress: formatCamelCase(item?.state ?? ""),
      schemaVersion: "",
      timestamp: FormatDate(item?.lastActivityAt, DATE_TIME_FORMAT),
      state: item?.state || "",
    })) || []),
  ];

  const currentRoutePath = useResolvedPath("").pathname;
  const pathSegments = currentRoutePath.split("/");
  const casesIndex = pathSegments.indexOf("cases");
  const newLocation = pathSegments.slice(0, casesIndex + 1).join("/");
  const getNavigationPath = (id: string, caseType: string) => {
    const path = isVerticalColumn ? `${currentRoutePath}/cases/${id}/case-type/${caseType}/active/v1/chat` : `${newLocation}/${id}/case-type/${caseType}/active/v1/chat`;
    return path;
  };

  const onCaseSelected = (selectedRowKey: Key, caseType: string) => {
    const selectedCase = formattedData.find((r) => r.key === selectedRowKey);
    setSelectedCase(selectedCase);
    const newUrl = getNavigationPath(selectedRowKey.toString(), caseType);
    navigate(newUrl, { replace: true });
  };

  return (
    <ComponentQueryLoader
      loading={
        identityVerificationCasesV1Loading ||
        credentialVerificationCasesV1Loading ||
        sendReportCasesV1Loading
      }
      error={
        identityVerificationCasesV1Error ||
        credentialVerificationCasesV1Error ||
        sendReportCasesV1Error
      }
      hasData={formattedData.length !== undefined}
      hasDataComponent={
        <SelectableList
          data={formattedData}
          selectedRecord={selectedCase}
          onSelectChange={onCaseSelected}
          isVerticalColumn={isVerticalColumn}
        />
      }
      noDataComponent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    />
  );
};
